"use strict"

const baseConversionBannerUrl =
  "https://storage.googleapis.com/classkick-assets/conversion-overlays/"

/**
 * Utility class for handling conversion overlay API calls
 */
export default class ConversionOverlay {
  /**
   * @ngInject
   */
  constructor($http, $log, $q) {
    /** @type {Object} */
    this._$http = $http
    /** @type {Object} */
    this._$q = $q
    this.$log = $log

    this._baseUrl = baseConversionBannerUrl
    this._cachedData = {}
    this._loading = {}
  }

  /**
   * @return {string}
   */
  get baseUrl() {
    return this._baseUrl
  }

  /**
   * @return {Object}
   */
  get cachedData() {
    return this._cachedData
  }

  /**
   * Get the full URL for an endpoint
   * @param {string} endpoint
   * @return {string}
   */
  _getUrl(endpoint = "") {
    return `${this._baseUrl}${endpoint}`
  }

  /**
   * Process and validate banner data
   * @param {Object} data - Raw banner data from API
   * @return {Object} Processed banner data
   */
  _processBannerData(data) {
    if (!data) {
      this.$log.error("invalid data")
      return undefined
    }

    return {
      ...data,
    }
  }

  /**
   * Fetch conversion banner data from the API
   * @param {string} endpoint - Optional endpoint to append to base URL
   * @return {Promise} Promise that resolves to JSON data
   */
  fetchBannerData(endpoint) {
    const cacheKey = endpoint

    // Return cached data if available
    if (this._cachedData[cacheKey]) {
      return this._$q.resolve(this._cachedData[cacheKey])
    }

    // Return existing promise if already loading
    if (this._loading[cacheKey]) {
      return this._loading[cacheKey]
    }

    const url = this._getUrl(endpoint)

    this._loading[cacheKey] = this._$http
      .get(url)
      .then((response) => {
        const processedData = this._processBannerData(response.data)
        this._cachedData[cacheKey] = processedData
        delete this._loading[cacheKey]
        return processedData
      })
      .catch((error) => {
        delete this._loading[cacheKey]
        this.$log.error("Error fetching conversion banner data:", error)
        return undefined
      })

    return this._loading[cacheKey]
  }

  /**
   * Fetch banner configuration
   * @return {Promise} Promise that resolves to banner config JSON
   */
  fetchBannerConfig() {
    return this.fetchBannerData("banner.json")
  }

  /**
   * Fetch banner configuration
   * @return {Promise} Promise that resolves to banner config JSON
   */
  fetchPopUpConfig() {
    return this.fetchBannerData("popup.json")
  }

  /**
   * Clear cached data
   * @param {string} endpoint - Optional specific endpoint to clear, clears all if not provided
   */
  clearCache(endpoint) {
    if (endpoint) {
      const cacheKey = endpoint
      delete this._cachedData[cacheKey]
      delete this._loading[cacheKey]
    } else {
      this._cachedData = {}
      this._loading = {}
    }
  }

  /**
   * Static method to create and show conversion banner utility
   * @param {Object} $http - Angular $http service
   * @param {Object} $q - Angular $q service
   * @return {ConversionOverlay} New instance
   */
  // static create($http, $log, $q) {
  //   return new ConversionOverlay($http, $log, $q);
  // }

  static create($http) {
    return new ConversionOverlay($http)
  }
}
